import React, { useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Chip,
  Tooltip,
  ClickAwayListener
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useTranslation } from 'react-i18next';

// ✅ Merkezi Customer tipi import
import type { Customer } from '../types/customer.types';
// Re-export for backward compatibility
export type { Customer };

// QuickSaleScreen.tsx'den veya ortak tiplerden alınacak
export interface PersonnelPermissions {
  changePrice?: boolean;
  applyDiscount?: boolean;
  cancel?: boolean;
  viewReports?: boolean;
  warehouseOperations?: boolean;
  viewAccountBalance?: boolean; // Bakiye görüntüleme yetkisi
  listAccounts?: boolean;      // Cari listeleme yetkisi
}

interface CustomerSelectionPanelProps {
  currentUserPermissions: PersonnelPermissions | null;
  customers: Customer[];
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer | null) => void;
  loadingCustomers: boolean;
  customerError: string | null;
  loadingUserPermissions: boolean;
  calculateBalance: (customer: Customer) => number;
  formatCurrency: (amount: number) => string;
  showAlertModal: (title: string, message: string) => void;
  refreshCustomers?: () => void;
  prepaidSystemEnabled?: boolean; // Yeni: Ön ödemeli sistem etkin mi?
  onOpenCustomerSelectionModal?: () => void; // Yeni: Müşteri seçim modalını açma fonksiyonu
  focusSearchBar?: () => void; // ✅ YENİ: Yenile butonuna tıklanınca fokuslan
  onOpenAddAccountModal?: () => void; // ✅ YENİ: Yeni cari ekleme modalını açma
  canAddAccount?: boolean; // ✅ YENİ: Yeni cari ekleme yetkisi
  customerNote?: string; // ✅ YENİ: Müşteri notu
  onOpenCustomerNoteModal?: () => void; // ✅ YENİ: Müşteri notu modalını açma
  isCartEmpty?: boolean; // ✅ YENİ: Sepet boş mu?
  isPackageMode?: boolean; // ✅ YENİ: Paket sipariş modu
  packageCustomerAddress?: string; // ✅ YENİ: Paket müşteri adresi
  onOpenCustomerQueryModal?: () => void;
  isPavoPayEnabled?: boolean;
  pavoPayTempCustomerName?: string | null;
  isEInvoiceCustomer?: boolean;
  onClearTempCustomer?: () => void;
}

const CustomerSelectionPanel: React.FC<CustomerSelectionPanelProps> = ({
  currentUserPermissions,
  customers,
  selectedCustomer,
  setSelectedCustomer,
  loadingCustomers,
  customerError,
  loadingUserPermissions,
  calculateBalance,
  formatCurrency,
  showAlertModal,
  refreshCustomers,
  prepaidSystemEnabled = false, // Varsayılan değer: false
  onOpenCustomerSelectionModal,
  focusSearchBar, // ✅ YENİ
  onOpenAddAccountModal, // ✅ YENİ: Yeni cari ekleme
  canAddAccount = false, // ✅ YENİ: Yeni cari ekleme yetkisi
  customerNote, // ✅ YENİ: Müşteri notu
  onOpenCustomerNoteModal, // ✅ YENİ: Müşteri notu modalını açma
  isCartEmpty = true, // ✅ YENİ: Sepet boş mu?
  isPackageMode = false, // ✅ YENİ: Paket sipariş modu
  packageCustomerAddress, // ✅ YENİ: Paket müşteri adresi
  onOpenCustomerQueryModal,
  isPavoPayEnabled = false,
  pavoPayTempCustomerName,
  isEInvoiceCustomer = false,
  onClearTempCustomer,
}) => {
  const { t } = useTranslation('salesSystem');

  // ✅ PAKET MODU: Sadeleştirilmiş görünüm (modal başlığında zaten müşteri adı ve telefon var)
  if (isPackageMode) {
    return (
      <Paper elevation={1} sx={{ p: 1, mb: 1, position: 'relative', bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {selectedCustomer ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                py: 0.5,
                px: 1,
              }}>
                <AccountCircleIcon sx={{ fontSize: '1.2rem', mr: 1, color: 'primary.main' }} />
                <Typography variant="body1" fontWeight="bold" color="primary.main">
                  {selectedCustomer.companyName}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                border: '1px dashed', 
                borderColor: 'primary.main', 
                p: 2, 
                borderRadius: 1, 
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}>
                <CircularProgress size={20} color="primary" />
                <Typography variant="body2" color="primary.main">
                  {t('customerSelectionPanel.loadingCustomerInfo')}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 1, mb: 1, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
          <AccountCircleIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
          {prepaidSystemEnabled ? t('customerSelectionPanel.prepaidCustomer') : t('customerSelectionPanel.currentAccount')}
        </Typography>
        
        <Box>
          {isPavoPayEnabled && onOpenCustomerQueryModal && (
            <Tooltip title={t('customerSelectionPanel.customerQuery')}>
              <IconButton 
                size="small" 
                onClick={onOpenCustomerQueryModal}
                sx={{ mr: 0.5 }}
                color="primary"
              >
                <ManageSearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {refreshCustomers && !loadingUserPermissions && currentUserPermissions?.listAccounts && (
            <Tooltip title={t('customerSelectionPanel.refreshAccounts')}>
              {loadingCustomers ? (
                <span>
                  <IconButton size="small" disabled>
                    <CircularProgress size={18} />
                  </IconButton>
                </span>
              ) : (
                <IconButton 
                  size="small" 
                  onClick={() => {
                    refreshCustomers();
                    // ✅ YENİ: Yenile butonuna tıklanınca fokuslan
                    if (focusSearchBar) {
                      setTimeout(() => {
                        focusSearchBar();
                      }, 100);
                    }
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              )}
            </Tooltip>
          )}
          
          {/* Müşteri/Cari seçim modalı butonu - Her zaman göster (yetki kontrolü yok) */}
          {onOpenCustomerSelectionModal ? (
            <Tooltip title={t('customerSelectionPanel.showAllAccounts')}>
              <IconButton 
                size="small" 
                onClick={() => {
                  console.log('[CustomerSelectionPanel] 🎯 Cari seç butonuna tıklandı!');
                  onOpenCustomerSelectionModal();
                }}
                sx={{ ml: 0.5 }}
              >
                <PersonSearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
              Modal handler yok!
            </Typography>
          )}
          
          {/* ✅ YENİ: Yeni Cari Kayıt Butonu */}
          {canAddAccount && onOpenAddAccountModal && (
            <Tooltip title={t('customerSelectionPanel.newAccount')}>
              <IconButton 
                size="small" 
                onClick={onOpenAddAccountModal}
                sx={{ ml: 0.5 }}
              >
                <PersonAddAlt1Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {/* ✅ YENİ: Müşteri Notu Butonu - Sepette ürün varsa aktif */}
          {onOpenCustomerNoteModal && (
            <Tooltip title={isCartEmpty ? t('customerSelectionPanel.noProductInCart') : (customerNote ? t('customerSelectionPanel.receiptNoteWithValue', { note: customerNote }) : t('customerSelectionPanel.addReceiptNote'))}>
              <span> {/* Disabled IconButton için span gerekli (Tooltip çalışması için) */}
                <IconButton 
                  size="small" 
                  onClick={onOpenCustomerNoteModal}
                  disabled={isCartEmpty}
                  sx={{ 
                    ml: 0.5,
                    color: customerNote ? 'primary.main' : 'inherit',
                    bgcolor: customerNote ? 'primary.light' : 'transparent',
                    '&:hover': {
                      bgcolor: customerNote ? 'primary.main' : 'action.hover',
                      color: customerNote ? 'white' : 'inherit',
                    },
                    '&.Mui-disabled': {
                      color: 'action.disabled',
                    }
                  }}
                >
                  <NoteAddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
      
      {!loadingUserPermissions && currentUserPermissions?.listAccounts ? (
        <Grid container spacing={1}>
            <Grid item xs={6}>
              <Autocomplete<Customer>
                options={currentUserPermissions?.listAccounts ? customers : []}
                getOptionLabel={(option) => option.companyName}
                value={selectedCustomer}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event: React.SyntheticEvent, newValue: Customer | null) => {
                  if (newValue) {
                    setSelectedCustomer(newValue);
                    if (pavoPayTempCustomerName && onClearTempCustomer) {
                      onClearTempCustomer();
                    }
                  } else {
                    // Kullanıcı Autocomplete'i temizlediğinde (X butonuna bastığında)
                    if (prepaidSystemEnabled && onOpenCustomerSelectionModal) {
                      // Ön ödemeli sistemde X'e basıldığında her zaman modalı aç
                      onOpenCustomerSelectionModal();
                      // Seçili müşteri null bırakılıyor çünkü modal açıldıktan sonra seçim yapılacak
                      return;
                    } else {
                      // Ön ödemeli modda değilse veya modal açma fonksiyonu yoksa, Genel Cari'ye dön
                      const generalCustomerAccount = customers.find(c => c.companyName === 'Genel Cari');
                      if (generalCustomerAccount) {
                        setSelectedCustomer(generalCustomerAccount);
                      } else {
                        console.warn("'Genel Cari' hesabı bulunamadığı için seçim sıfırlanamadı.");
                        setSelectedCustomer(null);
                        showAlertModal("Uyarı", "'Genel Cari' hesabı bulunamadı. Lütfen listeden bir cari seçin.");
                      }
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={prepaidSystemEnabled ? t('customerSelectionPanel.searchPrepaidCustomer') : t('customerSelectionPanel.searchCurrentAccount')}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                loading={loadingCustomers}
                disabled={!currentUserPermissions?.listAccounts}
              />
            </Grid>
            <Grid item xs={6}>
              {customerError && (
                <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{customerError}</Alert>
              )}
              {selectedCustomer && (
                <Box sx={{ border: '1px solid #e0e0e0', p: 1, borderRadius: 1, bgcolor: 'grey.50', height: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                        <strong style={{ marginRight: '8px' }}>{t('customerSelectionPanel.balance')}:</strong>
                    {loadingUserPermissions ? (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    ) : currentUserPermissions?.viewAccountBalance ? (
                      selectedCustomer.id === 'GENERAL_CUSTOMER_ACCOUNT' || selectedCustomer.companyName === 'Genel Cari' ? (
                        <Typography variant="body2" component="span">
                          {t('customerSelectionPanel.zeroBalance')}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            fontWeight: 'bold',
                            color: calculateBalance(selectedCustomer) >= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {formatCurrency(calculateBalance(selectedCustomer))}
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" component="span">
                        -
                      </Typography>
                    )}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        )
      : (
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonSearchIcon sx={{ mr: 1, fontSize: '1.1rem' }} /> 
          {prepaidSystemEnabled ? t('customerSelectionPanel.prepaidSelection') : t('customerSelectionPanel.accountSelection')}
        </Typography>
      )}

      {pavoPayTempCustomerName && (
        <Box sx={{ 
          mt: 1, 
          p: 0.75, 
          borderRadius: 1, 
          bgcolor: isEInvoiceCustomer ? 'success.50' : 'info.50',
          border: '1px solid',
          borderColor: isEInvoiceCustomer ? 'success.main' : 'info.main',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <ReceiptIcon sx={{ fontSize: '1rem', color: isEInvoiceCustomer ? 'success.main' : 'info.main', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('customerSelectionPanel.tempAccount')}: {pavoPayTempCustomerName}
            </Typography>
            <Chip 
              label={isEInvoiceCustomer ? t('customerSelectionPanel.eInvoice') : t('customerSelectionPanel.eArchive')} 
              size="small" 
              color={isEInvoiceCustomer ? 'success' : 'info'} 
              variant="filled"
              sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
            />
          </Box>
          <Tooltip title={t('customerSelectionPanel.removeTempAccount')}>
            <IconButton 
              size="small" 
              onClick={onClearTempCustomer}
              sx={{ p: 0.25, flexShrink: 0 }}
            >
              <CloseIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
};

export default CustomerSelectionPanel; 